import {
  AddComponentVariantCommand,
  canCreateComponent,
  canFrameNodes,
  canGroupNodes,
  canReplaceNodes,
  canUngroupNodes,
  ConvertFrameGroupCommand,
  CreateComponentCommand,
  DeleteComponentVariantCommand,
  DetachInstanceCommand,
  FlipNodesCommand,
  GroupNodesCommand,
  InsertNodeCommand,
  MoveNodesInTreeCommand,
  ReorderNodesCommand,
  ReplaceNodesCommand,
  type ReorderPlacement,
  UngroupNodesCommand,
  UpdateNodeBoundsCommand,
  UpdateInstanceVariantCommand,
} from "@/commands/nodeCommands";
import { UpsertPrototypeLinkCommand } from "@/commands/prototypeCommands";
import { TransactionCommand } from "@/commands/transaction";
import {
  createFrameNode,
  createPrimitiveNode,
  findFirstFrameNode,
  findSceneNode,
  getAllSceneNodes,
  getSceneNodeChildren,
  type CurvaDocument,
} from "@/document/model/document";
import {
  createIdentityTransform,
  type BooleanNode,
  type BooleanOperation,
  type GroupNode,
  type SceneNode,
  type VectorNetworkNode,
} from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { getVectorNetworkBounds } from "@/document/operations/vectorGeometry";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { componentVariants, createInstanceFromComponent } from "@/document/operations/componentSystem";
import { applyNodeLocalPlacementFromWorld } from "@/document/operations/nodeWorldTransform";
import { materializeShapeBuilderNetwork, type ShapeBuilderSelection } from "@/document/operations/shapeBuilder";
import { planarizeVectorNetwork } from "@/document/operations/vectorPlanarization";
import type { BooleanOperandContours } from "@/document/operations/booleanGeometry";
import { resolveBooleanGeometryWithPathOps } from "@/document/operations/pathBooleanGeometry";
import { boundsFromContours } from "@/document/operations/pathGeometry";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { frameCaptureSiblingIds } from "@/editor/shell/frameCapture";
import { getAlignmentReferenceBounds, getAlignmentUpdates, alignmentLabels, type AlignmentAction } from "@/editor/shell/right-panel/alignment";
import {
  childrenCountForParent,
  imageRectangleBoundsAtPoint,
} from "@/editor/shell/shellTransfer";
import {
  nodesInLayerOrder,
  reorderMessage,
  titleCase,
  topFrameIdAtPoint,
} from "@/editor/shell/shellSelection";
import {
  createPenVectorNetworkNode,
  createPencilVectorNetworkNode,
  flattenOutputStyleForNodes,
  nodeToBooleanOperand,
  nodesToFlattenVectorNetwork,
  nodesToOutlinedStrokeVectorNodes,
} from "@/editor/shell/shellVectorConversion";
import { vectorizePencilStroke } from "@/editor/tools/pencilVectorize";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import type { FrameTemplate } from "../../shell/frameTemplates";
import { insertionPointAboveNode } from "@/editor/shell/shellTreeHelpers";
import { mirrorSelectedVectorGeometry } from "./nudgeHandlers";
import type { EditorCommandHost } from "../editorCommandHost";

type Point = { x: number; y: number };

export interface StructureHandlerOptions {
  getActivePageId: () => string | undefined;
  getZoom?: () => number;
  onDuplicateContinuationReset?: () => void;
  onComponentCreated?: (document: CurvaDocument, componentId: string) => void;
}

export function placeNodeInParent(parentId: string, node: SceneNode, worldBounds: Rect): SceneNode {
  return applyNodeLocalPlacementFromWorld(documentStore.getDocument(), parentId, node, worldBounds, node.transform.rotation ?? 0);
}

export function drawPrimitive(
  host: EditorCommandHost,
  kind: PrimitiveKind,
  bounds: Rect,
  parentFrameId: string | undefined,
  options: StructureHandlerOptions,
): void {
  const parentId = parentFrameId ?? options.getActivePageId();
  if (!parentId) {
    uiStore.setMessage("No canvas available");
    return;
  }
  const context = host.commandContext();
  const parentFrame = findSceneNode(context.document, parentId);
  const activePage = context.document.pages.find(page => page.id === parentId);
  const count = parentFrame?.type === "frame" ? parentFrame.children.length : activePage?.children.length ?? 0;
  const label = titleCase(kind);
  const node = placeNodeInParent(parentId, createPrimitiveNode(kind, `${label} ${count + 1}`, bounds.x, bounds.y, bounds.width, bounds.height), bounds);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [node.id], primarySelectedNodeId: node.id, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new InsertNodeCommand(parentId, node), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setActiveTool("select");
  host.commit(context.document, `Drew ${node.name}`);
}

export function drawFrame(
  host: EditorCommandHost,
  bounds: Rect,
  parentFrameId: string | undefined,
  name: string | undefined,
  options: StructureHandlerOptions,
): void {
  const parentId = parentFrameId ?? options.getActivePageId();
  if (!parentId) {
    uiStore.setMessage("No canvas available");
    return;
  }
  const context = host.commandContext();
  const parentFrame = findSceneNode(context.document, parentId);
  const activePage = context.document.pages.find(page => page.id === parentId);
  const siblings = parentFrame ? getSceneNodeChildren(parentFrame) : activePage?.children;
  const count = siblings?.filter(node => node.type === "frame").length ?? 0;
  const node = placeNodeInParent(parentId, createFrameNode(name ?? `Frame ${count + 1}`, bounds.x, bounds.y, bounds.width, bounds.height), bounds);
  const capturedNodeIds = frameCaptureSiblingIds(context.document, parentId, bounds);
  const command = capturedNodeIds.length
    ? new TransactionCommand(`Created ${node.name}`, [new InsertNodeCommand(parentId, node), new MoveNodesInTreeCommand(capturedNodeIds, { parentId: node.id, parentType: "frame", index: 0 })])
    : new InsertNodeCommand(parentId, node);
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [node.id], primarySelectedNodeId: node.id, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(command, context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setActiveTool("select");
  host.commit(context.document, `Created ${node.name}`);
}

export function insertFrameTemplate(host: EditorCommandHost, template: FrameTemplate, options: StructureHandlerOptions): void {
  const frameCount = getAllSceneNodes(documentStore.getDocument()).filter(node => node.type === "frame").length;
  const offset = frameCount * 24;
  drawFrame(host, { x: 96 + offset, y: 80 + offset, width: template.width, height: template.height }, undefined, template.name, options);
}

export function createPrototypeLink(host: EditorCommandHost, sourceNodeId: string, targetFrameId: string): void {
  const source = findSceneNode(documentStore.getDocument(), sourceNodeId);
  const target = findSceneNode(documentStore.getDocument(), targetFrameId);
  if (!source || target?.type !== "frame") {
    uiStore.setMessage("Prototype links must target a frame");
    return;
  }
  const context = host.commandContext();
  host.history.execute(new UpsertPrototypeLinkCommand({ id: createId("prototype"), sourceNodeId, targetFrameId }), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, `Linked ${source.name} to ${target.name}`);
}

/**
 * Places a component instance centered on a document-space point.
 * Optional `parentId` forces the drop parent (otherwise deepest frame under the point).
 * Returns the new instance id.
 */
export function insertComponentInstance(
  host: EditorCommandHost,
  componentId: string,
  point: Point,
  options: StructureHandlerOptions,
  variantId?: string,
  parentId?: string,
): string {
  const document = documentStore.getDocument();
  const component = findSceneNode(document, componentId);
  if (!component || component.type !== "component") {
    throw new Error(`Component "${componentId}" was not found.`);
  }
  const selectedVariant = variantId
    ? componentVariants(component).find(variant => variant.id === variantId)
    : componentVariants(component)[0];
  if (!selectedVariant) {
    throw new Error(variantId
      ? `Variant "${variantId}" was not found on component "${componentId}".`
      : `Component "${componentId}" has no variants.`);
  }
  const resolvedParentId = parentId
    ?? topFrameIdAtPoint(document, point, options.getActivePageId())
    ?? options.getActivePageId();
  if (!resolvedParentId) {
    throw new Error("No canvas available for insert_instance.");
  }
  if (parentId) {
    const parent = findSceneNode(document, parentId);
    const page = document.pages.find(candidate => candidate.id === parentId);
    if (!page && parent?.type !== "frame") {
      throw new Error(`Parent "${parentId}" must be a page or frame.`);
    }
  }

  // Center the variant on `point` in document space, then run the same
  // placeNodeInParent path as create_text / drawPrimitive (handles ancestor
  // rotation). Always return the inserted id so callers don't depend on selection.
  const worldBounds: Rect = {
    x: point.x - selectedVariant.bounds.width / 2,
    y: point.y - selectedVariant.bounds.height / 2,
    width: selectedVariant.bounds.width,
    height: selectedVariant.bounds.height,
  };
  const instance = createInstanceFromComponent(component, {
    x: worldBounds.x - selectedVariant.bounds.x,
    y: worldBounds.y - selectedVariant.bounds.y,
  }, variantId);
  const placed = placeNodeInParent(resolvedParentId, instance, worldBounds);
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [placed.id], primarySelectedNodeId: placed.id, mode: "select", selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new InsertNodeCommand(resolvedParentId, placed, childrenCountForParent(context.document, resolvedParentId)), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  selectionStore.setActiveTool("select");
  host.commit(context.document, `Inserted ${placed.name}`);
  return placed.id;
}

export function createComponent(host: EditorCommandHost, nodeIds: string[], options: StructureHandlerOptions): void {
  const context = host.commandContext();
  const componentNodeIds = nodeIds.filter(id => !isNodeLockedForEditing(context.document, id));
  if (!canCreateComponent(context.document, componentNodeIds)) {
    uiStore.setMessage("Select an object to create a component");
    return;
  }
  const componentId = createId("component");
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [componentId], primarySelectedNodeId: componentId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new CreateComponentCommand(componentNodeIds, componentId), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  options.onDuplicateContinuationReset?.();
  host.commit(context.document, "Created component");
  options.onComponentCreated?.(context.document, componentId);
}

export function addComponentVariant(host: EditorCommandHost, componentId: string, options: StructureHandlerOptions): void {
  const context = host.commandContext();
  host.history.execute(new AddComponentVariantCommand(componentId), context, { afterSelection: host.currentSelection() });
  options.onDuplicateContinuationReset?.();
  host.commit(context.document, "Added variant");
}

export function deleteComponentVariant(host: EditorCommandHost, componentId: string, variantId: string, options: StructureHandlerOptions): void {
  const context = host.commandContext();
  try {
    host.history.execute(new DeleteComponentVariantCommand(componentId, variantId), context, { afterSelection: host.currentSelection() });
    options.onDuplicateContinuationReset?.();
    host.commit(context.document, "Deleted variant");
  } catch (error) {
    uiStore.setMessage(error instanceof Error ? error.message : "Could not delete variant");
  }
}

export function updateInstanceVariant(host: EditorCommandHost, instanceId: string, variantId: string, options: StructureHandlerOptions): void {
  const context = host.commandContext();
  host.history.execute(new UpdateInstanceVariantCommand(instanceId, variantId), context, { afterSelection: host.currentSelection() });
  options.onDuplicateContinuationReset?.();
  host.commit(context.document, "Swapped instance variant");
}

export function detachInstance(host: EditorCommandHost, instanceId: string, options: StructureHandlerOptions): void {
  const node = findSceneNode(documentStore.getDocument(), instanceId);
  if (node?.type !== "instance") {
    uiStore.setMessage("Select an instance to detach");
    return;
  }
  const groupId = createId("group");
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({
    selectedNodeIds: [groupId], primarySelectedNodeId: groupId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined,
  });
  host.history.execute(new DetachInstanceCommand(instanceId, groupId), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  options.onDuplicateContinuationReset?.();
  host.commit(context.document, "Detached instance");
}

export function reorderNodes(host: EditorCommandHost, nodeIds: string[], placement: ReorderPlacement): void {
  const document = documentStore.getDocument();
  const ids = nodeIds.filter(id => !isNodeLockedForEditing(document, id));
  if (!ids.length) return;
  const context = host.commandContext();
  host.history.execute(new ReorderNodesCommand(ids, placement), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, reorderMessage(ids.length, placement));
}

export function reorderFlexChild(host: EditorCommandHost, nodeId: string, parentFrameId: string, index: number): void {
  const document = documentStore.getDocument();
  if (isNodeLockedForEditing(document, nodeId)) return;
  const parent = findSceneNode(document, parentFrameId);
  if (parent?.type !== "frame") return;
  const currentIndex = parent.children.findIndex(child => child.id === nodeId);
  if (currentIndex < 0 || currentIndex === index) return;
  const context = host.commandContext();
  host.history.execute(new MoveNodesInTreeCommand([nodeId], { parentId: parentFrameId, parentType: "frame", index: index > currentIndex ? index + 1 : index }), context, { afterSelection: host.currentSelection() });
  host.commit(context.document, "Reordered flex child");
}

export function groupSelectedNodes(host: EditorCommandHost): void {
  const document = documentStore.getDocument();
  const ids = host.currentSelection().selectedNodeIds.filter(id => !isNodeLockedForEditing(document, id));
  if (!canGroupNodes(document, ids)) {
    uiStore.setMessage("Select two or more objects to group");
    return;
  }
  const groupId = createId("group");
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({ selectedNodeIds: [groupId], primarySelectedNodeId: groupId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new GroupNodesCommand(ids, groupId), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Grouped selection");
}

export function frameSelectedNodes(host: EditorCommandHost): void {
  const document = documentStore.getDocument();
  const ids = host.currentSelection().selectedNodeIds.filter(id => !isNodeLockedForEditing(document, id));
  if (!canFrameNodes(document, ids)) {
    uiStore.setMessage("Select an object to frame");
    return;
  }
  const frameId = createId("frame");
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({ selectedNodeIds: [frameId], primarySelectedNodeId: frameId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new GroupNodesCommand(ids, frameId, "frame"), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, "Framed selection");
}

export function ungroupSelectedNodes(host: EditorCommandHost): void {
  const document = documentStore.getDocument();
  const ids = host.currentSelection().selectedNodeIds.filter(id => !isNodeLockedForEditing(document, id));
  if (!canUngroupNodes(document, ids)) {
    uiStore.setMessage("Select a group to ungroup");
    return;
  }
  const groups = getAllSceneNodes(document).filter((node): node is GroupNode => ids.includes(node.id) && node.type === "group");
  const childIds = groups.flatMap(group => group.children.map(child => child.id));
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({ selectedNodeIds: childIds, primarySelectedNodeId: childIds.at(-1), mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new UngroupNodesCommand(ids), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, groups.length > 1 ? `Ungrouped ${groups.length} groups` : "Ungrouped group");
}

export function convertFrameGroup(host: EditorCommandHost, nodeId: string): void {
  const document = documentStore.getDocument();
  const node = findSceneNode(document, nodeId);
  if ((node?.type !== "frame" && node?.type !== "group") || isNodeLockedForEditing(document, nodeId)) {
    uiStore.setMessage("Select a frame or group to convert");
    return;
  }
  const targetType = node.type === "frame" ? "group" : "frame";
  const context = host.commandContext();
  const nextSelection = host.selectionSnapshot({ selectedNodeIds: [nodeId], primarySelectedNodeId: nodeId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new ConvertFrameGroupCommand(nodeId, targetType), context, { afterSelection: nextSelection });
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, targetType === "frame" ? "Converted to frame" : "Converted to group");
}

export function flipSelectedNodes(host: EditorCommandHost, axis: "horizontal" | "vertical", selectionBounds: Rect | undefined): void {
  // In vector edit mode, mirror the selected vertices about their own bounding
  // box instead of flipping the whole shape.
  if (mirrorSelectedVectorGeometry(host, axis)) return;
  const selection = host.currentSelection();
  if (!selectionBounds || !selection.selectedNodeIds.length || selection.mode === "textEdit") return;
  const document = documentStore.getDocument();
  const nodes = getAllSceneNodes(document).filter(node => selection.selectedNodeIds.includes(node.id) && !isNodeLockedForEditing(document, node.id));
  if (!nodes.length) return;
  const context = host.commandContext();
  host.history.execute(new FlipNodesCommand(nodes.map(node => node.id), selectionBounds, axis), context, { afterSelection: selection });
  host.commit(context.document, axis === "horizontal" ? "Flipped horizontal" : "Flipped vertical");
}

export function alignSelectedNodes(host: EditorCommandHost, action: AlignmentAction, selectionBounds: Rect | undefined): void {
  const selection = host.currentSelection();
  if (!selection.selectedNodeIds.length) return;
  const document = documentStore.getDocument();
  const nodes = getAllSceneNodes(document).filter(node => selection.selectedNodeIds.includes(node.id) && !isNodeLockedForEditing(document, node.id));
  const reference = getAlignmentReferenceBounds(document, selection.selectedNodeIds, selectionBounds);
  if (!nodes.length || !reference) return;
  const commands = getAlignmentUpdates(nodes, reference, action)
    .map(update => ({ node: nodes.find(node => node.id === update.nodeId), update }));
  const nodeCommands = commands.flatMap(({ node, update }) => node ? [new UpdateNodeBoundsCommand(node.id, node.bounds, update.bounds)] : []);
  host.runDocumentCommands(nodeCommands, alignmentLabels[action]);
}

export async function applyBooleanOperation(host: EditorCommandHost, operation: BooleanOperation): Promise<void> {
  if (host.currentSelection().mode === "textEdit") return;
  const document = documentStore.getDocument();
  const ids = host.currentSelection().selectedNodeIds.filter(id => !isNodeLockedForEditing(document, id));
  if (ids.length < 2 || !canReplaceNodes(document, ids)) {
    uiStore.setMessage("Select two or more sibling objects");
    return;
  }
  try {
    const nodes = nodesInLayerOrder(document, ids);
    const operands = await Promise.all(nodes.map(nodeToBooleanOperand));
    const usable = operands.filter((operand): operand is BooleanOperandContours => operand.contours.length > 0);
    if (usable.length < 2) {
      uiStore.setMessage("Boolean operations need closed vectorizable objects");
      return;
    }
    const resolved = await resolveBooleanGeometryWithPathOps(operation, usable);
    if (!resolved.contours.length) {
      uiStore.setMessage("Boolean result is empty");
      return;
    }
    const booleanId = createId("boolean");
    const base = nodes[0]!;
    const node: BooleanNode = {
      id: booleanId, name: titleCase(operation), type: "boolean", visible: true, locked: false, transform: createIdentityTransform(), bounds: resolved.bounds, style: structuredClone(base.style), operation,
      sourceNodes: nodes.map(node => structuredClone(node)),
      sourceGeometry: usable.map(operand => ({ nodeId: operand.nodeId, sourceBounds: operand.sourceBounds, sourceKey: operand.sourceKey, bounds: boundsFromContours(operand.contours), path: operand.path, contours: operand.contours.map(contour => contour.map(point => ({ ...point }))) })),
      resolved: { path: resolved.path, contours: resolved.contours },
    };
    const context = host.commandContext();
    const next = host.selectionSnapshot({ selectedNodeIds: [booleanId], primarySelectedNodeId: booleanId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
    host.history.execute(new ReplaceNodesCommand(`Boolean ${titleCase(operation)}`, ids, [node]), context, { afterSelection: next });
    host.applySelectionSnapshot(next);
    host.commit(context.document, `${titleCase(operation)} created`);
  } catch (error) {
    console.error("Boolean operation failed", error);
    uiStore.setMessage(error instanceof Error ? error.message : "Boolean operation failed");
  }
}

export async function flattenSelectedNodes(host: EditorCommandHost, options: { planarize?: boolean; nodeIds?: string[] } = {}): Promise<void> {
  if (host.currentSelection().mode === "textEdit") return;
  const document = documentStore.getDocument();
  const ids = (options.nodeIds ?? host.currentSelection().selectedNodeIds).filter(id => !isNodeLockedForEditing(document, id));
  if (!ids.length || !canReplaceNodes(document, ids)) {
    uiStore.setMessage("Select sibling objects to flatten");
    return;
  }
  try {
    const nodes = nodesInLayerOrder(document, ids);
    const network = await nodesToFlattenVectorNetwork(nodes);
    const output = options.planarize ? planarizeVectorNetwork(network) : network;
    if (!output.vertices.length || !output.edges.length) {
      uiStore.setMessage("Selection has no vectorizable closed outlines");
      return;
    }
    const vectorId = createId("node");
    const suffix = options.planarize ? "Planarized" : "Flattened";
    const node: VectorNetworkNode = { id: vectorId, name: nodes.length > 1 ? `${options.planarize ? "Planarize" : "Flatten"} ${nodes.length} objects` : `${nodes[0]!.name} ${suffix}`, type: "vectorNetwork", visible: true, locked: false, transform: createIdentityTransform(), bounds: getVectorNetworkBounds(output), style: flattenOutputStyleForNodes(nodes), network: output };
    const context = host.commandContext();
    const next = host.selectionSnapshot({ selectedNodeIds: [vectorId], primarySelectedNodeId: vectorId, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
    host.history.execute(new ReplaceNodesCommand(options.planarize ? "Flatten and Planarize Selection" : "Flatten Selection", ids, [node]), context, { afterSelection: next });
    host.applySelectionSnapshot(next);
    host.commit(context.document, options.planarize ? "Flattened and planarized selection" : "Flattened selection");
  } catch (error) {
    console.error("Flatten failed", error);
    uiStore.setMessage(error instanceof Error ? error.message : "Flatten failed");
  }
}

export async function outlineStrokeForSelection(host: EditorCommandHost, nodeIds?: string[]): Promise<void> {
  if (host.currentSelection().mode === "textEdit") return;
  const document = documentStore.getDocument();
  const ids = (nodeIds ?? host.currentSelection().selectedNodeIds).filter(id => !isNodeLockedForEditing(document, id));
  if (!ids.length || !canReplaceNodes(document, ids)) {
    uiStore.setMessage("Select sibling objects with strokes");
    return;
  }
  try {
    const outlined = await nodesToOutlinedStrokeVectorNodes(nodesInLayerOrder(document, ids));
    if (!outlined.length) {
      uiStore.setMessage("Selection has no outlineable strokes");
      return;
    }
    const nextIds = outlined.map(node => node.id);
    const context = host.commandContext();
    const next = host.selectionSnapshot({ selectedNodeIds: nextIds, primarySelectedNodeId: nextIds.at(-1), mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
    host.history.execute(new ReplaceNodesCommand("Outline Stroke", ids, outlined), context, { afterSelection: next });
    host.applySelectionSnapshot(next);
    host.commit(context.document, outlined.length > 1 ? `Outlined ${outlined.length} strokes` : "Outlined stroke");
  } catch (error) {
    console.error("Outline stroke failed", error);
    uiStore.setMessage(error instanceof Error ? error.message : "Outline stroke failed");
  }
}

export function createPenNetwork(host: EditorCommandHost, point: Point, options: StructureHandlerOptions): { nodeId: string; vertexId: string } | undefined {
  const document = documentStore.getDocument();
  const parentId = topFrameIdAtPoint(document, point, options.getActivePageId()) ?? findFirstFrameNode(document)?.id;
  if (!parentId) {
    uiStore.setMessage("No frame available");
    return;
  }
  const vertexId = createId("vertex");
  const parent = findSceneNode(document, parentId);
  const count = parent?.type === "frame" ? parent.children.length : findFirstFrameNode(document)?.children.length ?? 0;
  const node = placeNodeInParent(parentId, createPenVectorNetworkNode(`Path ${count + 1}`, vertexId, point), { x: point.x, y: point.y, width: 0, height: 0 });
  const context = host.commandContext();
  const next = host.selectionSnapshot({ selectedNodeIds: [node.id], primarySelectedNodeId: node.id, mode: "vectorEdit", selectedVertexId: vertexId, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new InsertNodeCommand(parentId, node), context, { afterSelection: next });
  selectionStore.setActiveTool("path");
  host.applySelectionSnapshot(next);
  host.commit(context.document, `Started ${node.name}`);
  return { nodeId: node.id, vertexId };
}

export function drawPencilStroke(host: EditorCommandHost, points: Point[], options: StructureHandlerOptions): void {
  const document = documentStore.getDocument();
  const parentId = topFrameIdAtPoint(document, points[0] ?? { x: 0, y: 0 }, options.getActivePageId()) ?? findFirstFrameNode(document)?.id;
  if (!parentId) {
    uiStore.setMessage("No frame available");
    return;
  }
  const network = vectorizePencilStroke(points);
  if (!network) {
    uiStore.setMessage("Stroke too short");
    return;
  }
  const parent = findSceneNode(document, parentId);
  const count = parent?.type === "frame" ? parent.children.length : findFirstFrameNode(document)?.children.length ?? 0;
  const node = placeNodeInParent(parentId, createPencilVectorNetworkNode(`Pencil ${count + 1}`, network), getVectorNetworkBounds(network));
  const context = host.commandContext();
  const next = host.selectionSnapshot({ selectedNodeIds: [node.id], primarySelectedNodeId: node.id, mode: "select", selectedVertexId: undefined, selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new InsertNodeCommand(parentId, node), context, { afterSelection: next });
  host.applySelectionSnapshot(next);
  selectionStore.setActiveTool("pencil");
  host.commit(context.document, `Drew ${node.name}`);
}

export function commitShapeBuilderSelection(host: EditorCommandHost, nodeId: string, selection: ShapeBuilderSelection): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork") {
    uiStore.setMessage("Shape builder needs an editable vector network");
    return;
  }
  const network = materializeShapeBuilderNetwork(node.network, selection);
  if (!network || !network.vertices.length || !network.edges.length) {
    uiStore.setMessage("Shape builder result is empty");
    return;
  }
  const insertion = insertionPointAboveNode(documentStore.getDocument(), node.id);
  if (!insertion) {
    uiStore.setMessage("Shape builder could not place result");
    return;
  }
  const resultId = createId("node");
  const result: VectorNetworkNode = { ...structuredClone(node), id: resultId, name: `${node.name} Shape Builder`, bounds: getVectorNetworkBounds(network), network };
  const context = host.commandContext();
  const next = host.selectionSnapshot({ selectedNodeIds: [resultId], primarySelectedNodeId: resultId, mode: "vectorEdit", selectedVertexId: undefined, selectedVertexIds: [], selectedEdgeIds: [], selectedHandle: undefined });
  host.history.execute(new InsertNodeCommand(insertion.parentId, result, insertion.index), context, { afterSelection: next });
  host.applySelectionSnapshot(next);
  selectionStore.setActiveTool("select");
  host.commit(context.document, "Shape builder result");
}
